import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { headingLevels: { type: String, default: "h2,h3" } }

  connect() {
    const selector = this.headingLevelsValue.split(",").map(l => l.trim()).join(",")
    this.headings = Array.from(this.element.querySelectorAll(selector))
    if (this.headings.length === 0) return

    this.assignIds()
    this.buildUI()
    this.setupObserver()
  }

  disconnect() {
    this.observer?.disconnect()

    // Unwrap: move children back out of the flex wrapper
    if (this.wrapper && this.wrapper.parentNode) {
      this.aside?.remove()
      this.contentWrapper?.remove()

      // Move original children back to the element
      while (this.wrapper.firstChild) {
        this.element.appendChild(this.wrapper.firstChild)
      }
      this.wrapper.remove()
      this.wrapper = null
    }
  }

  assignIds() {
    const usedIds = new Set()
    this.headings.forEach(heading => {
      if (!heading.id) {
        let base = heading.textContent.trim().toLowerCase()
          .replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-")
        if (!base) base = "heading"
        let id = base
        let i = 1
        while (usedIds.has(id) || document.getElementById(id)) {
          id = `${base}-${i++}`
        }
        heading.id = id
      }
      usedIds.add(heading.id)
    })
  }

  buildUI() {
    // Wrap existing page children in a flex container
    this.wrapper = document.createElement("div")
    this.wrapper.className = "flex gap-8"

    this.contentWrapper = document.createElement("div")
    this.contentWrapper.className = "flex-1 min-w-0"

    // Move all existing children into the content wrapper
    while (this.element.firstChild) {
      this.contentWrapper.appendChild(this.element.firstChild)
    }

    // Build the aside sidebar
    this.aside = document.createElement("aside")
    this.aside.className = "hidden md:block w-64 shrink-0 sticky top-[140px] self-start overflow-y-auto max-h-[calc(100vh-160px)] print:hidden border-l border-slate-200 dark:border-slate-700 pl-4"
    this.aside.innerHTML = this.sidebarHTML()

    this.wrapper.appendChild(this.contentWrapper)
    this.wrapper.appendChild(this.aside)
    this.element.appendChild(this.wrapper)

    // Link clicks
    this.aside.querySelectorAll("a[href^='#']").forEach(link => {
      link.addEventListener("click", (e) => this.handleLinkClick(e))
    })

    this.links = Array.from(this.aside.querySelectorAll("a[href^='#']"))
  }

  sidebarHTML() {
    const levelMap = { H1: 0, H2: 1, H3: 2, H4: 3, H5: 4, H6: 5 }
    const minLevel = Math.min(...this.headings.map(h => levelMap[h.tagName] ?? 0))

    const buildList = (headings, startIdx, parentLevel) => {
      const isRoot = parentLevel === minLevel
      let html = `<ul class="list-none m-0 ${isRoot ? 'p-0' : 'pl-4'}">`
      let i = startIdx
      while (i < headings.length) {
        const h = headings[i]
        const level = levelMap[h.tagName] ?? 0
        if (level < parentLevel) break
        if (level === parentLevel) {
          html += '<li>'
          html += `<a href="#${h.id}" class="block py-1.5 px-3 text-sm text-slate-600 dark:!text-gray-200 hover:text-[var(--link-color)] dark:hover:!text-gray-50 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors truncate" title="${h.textContent.trim()}">${h.textContent.trim()}</a>`
          if (i + 1 < headings.length && (levelMap[headings[i + 1].tagName] ?? 0) > parentLevel) {
            const result = buildList(headings, i + 1, levelMap[headings[i + 1].tagName] ?? 0)
            html += result.html
            i = result.nextIdx
          } else {
            i++
          }
          html += '</li>'
        } else {
          const result = buildList(headings, i, level)
          html += result.html
          i = result.nextIdx
        }
      }
      html += '</ul>'
      return { html, nextIdx: i }
    }

    const { html: navItems } = buildList(this.headings, 0, minLevel)

    return `
      <h2 class="text-sm font-semibold px-3 mb-2 dark:!text-slate-200">Table of Contents</h2>
      <nav>${navItems}</nav>
    `
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.activateLink(entry.target.id)
          }
        })
      },
      { rootMargin: "0px 0px -70% 0px", threshold: 0 }
    )
    this.headings.forEach(h => this.observer.observe(h))
  }

  activateLink(id) {
    this.links?.forEach(link => {
      const isActive = link.getAttribute("href") === `#${id}`
      link.classList.toggle("bg-slate-100", isActive)
      link.classList.toggle("dark:bg-slate-700", isActive)
      link.classList.toggle("font-medium", isActive)
      link.classList.toggle("text-[var(--link-color)]", isActive)
      link.classList.toggle("dark:!text-gray-50", isActive)
      if (isActive) {
        link.scrollIntoView({ block: "nearest" })
      }
    })
  }

  handleLinkClick(e) {
    e.preventDefault()
    const id = e.currentTarget.getAttribute("href").slice(1)
    const target = document.getElementById(id)
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }
}
